import {
  Select,
  Spin,
  Card,
  Button,
  message,
  Modal,
  Row,
  Upload,
  Col,
  Pagination,
  Image,
  Space,
  Typography,
  Switch,
  Empty,
  Tag,
  Badge,
} from 'antd'
import { useEffect, useState } from 'react'
import {
  getBotPost,
  deleteContentById,
  updateBotPost,
  enableComment,
} from 'src/api/social'
import { unreadCountNotification } from 'src/api/notification'
import { getBots, getUsers } from 'src/api/user'
import { downloadImageById } from 'src/api/image'
import { fetchCountryProvider } from 'src/api/authen'
import MentionEmojiInput from 'src/components/Input/MentionsEmojiInput'
import {
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  LikeOutlined,
  CommentOutlined,
  ShareAltOutlined,
  UnlockOutlined,
  LockOutlined,
  BellOutlined,
  PushpinFilled,
  PushpinOutlined,
} from '@ant-design/icons'
import { extractTextContent } from 'src/utils/stringUtil'
import { useNavigate } from 'react-router-dom'
import 'tailwindcss/tailwind.css'
import Notification from './Notifications'

const { confirm } = Modal
const { Text } = Typography

const BotPost = () => {
  const navigate = useNavigate()
  const [providerCountry, setProviderCountry] = useState('TN')
  const [countries, setCountries] = useState([])
  const [userData, setUserData] = useState([])
  const [userId, setUserId] = useState(undefined)
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [editRecord, setEditRecord] = useState(null)
  const [editText, setEditText] = useState('')
  const [newAttachments, setNewAttachments] = useState([])
  const [deletedAttachments, setDeletedAttachments] = useState([])
  const [mentionMap, setMentionMap] = useState({})
  const [imageLoadErrors, setImageLoadErrors] = useState({})
  const [loading, setLoading] = useState(true)
  const [transformedData, setTransformedData] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)
  const [previewImage, setPreviewImage] = useState(null)
  const [previewVisible, setPreviewVisible] = useState(false)
  const [notificationVisible, setNotificationVisible] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)

  const params = new URLSearchParams()
  const allowedFileTypes = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ]

  const handleClickOpen = (record) => {
    params.append('botUserId', userId)
    params.append('text', record.text)
    navigate(`/social/bot-post/comments/${record.id}?${params.toString()}`)
  }

  const handleTogglePrivacy = async (record) => {
    try {
      setLoading(true)
      const nextType = record.type === 'PRIVATE' ? 'PUBLIC' : 'PRIVATE'
      await updateBotPost({
        id: record.id,
        type: nextType,
        text: record.text,
      })
      message.success(`Post is now ${nextType.toLocaleLowerCase()}.`)
      await fetchBotPostData(currentPage, pageSize, userId, providerCountry)
    } catch (err) {
      message.error('Failed to change post visibility.')
      console.log('err', err)
    } finally {
      setLoading(false)
    }
  }

  const transformData = (data) => {
    if (!data) return []
    return data.map((item) => ({
      ...item,
      key: item.id,
      createdOn: convertToCustomFormat(item.created_on),
      resourceContents: null,
      loadingAttachments: item.attachments?.length > 0,
      formattedText: item.text ? extractTextContent(item.text) : '',
      like_count: item.like_count || 0,
      comment_count: item.comment_count || 0,
      share_count: item.share_count || 0,
    }))
  }

  const fetchImagesForRows = async (rows) => {
    const updatedRows = await Promise.all(
      rows.map(async (row) => {
        if (row.loadingAttachments) {
          const images = await getImages(row.attachments, row.id)
          return {
            ...row,
            resourceContents: images,
            loadingAttachments: false,
          }
        }
        return row
      })
    )
    setTransformedData(updatedRows)
  }

  const handleInputChange = (e) => {
    setUserId(e)
  }

  const getImages = async (attachments, postId) => {
    const fileContents = []
    if (!attachments) return fileContents
    for (const attachment of attachments) {
      fileContents.push({
        id: attachment.attachmentId,
        content: await downloadImageById(attachment.attachmentId),
      })
    }
    return fileContents
  }

  const handleDelete = async (record) => {
    try {
      setLoading(true)
      await deleteContentById(record, 'BOT-POST')
      message.success('Content deleted successfully!')
      const newTotalRecords = totalRecords - 1
      const newPage =
        currentPage > 1 && newTotalRecords % pageSize === 0
          ? currentPage - 1
          : currentPage
      await fetchBotPostData(newPage, pageSize, userId, providerCountry)
      setCurrentPage(newPage)
    } catch (error) {
      message.error('Failed to delete content.')
    } finally {
      setLoading(false)
    }
  }

  const fetchBotUser = async () => {
    const response = await getBots()
    const usersList = response.data.map((element) => ({
      label: element.username,
      value: element.user_id,
    }))
    setUserId(usersList[0]?.value)
    setUserData(usersList)
    return usersList
  }

  const fetchBotPostData = async (page, size, userId, providerCountry) => {
    try {
      setLoading(true)
      const response = await getBotPost(page - 1, size, providerCountry, userId)
      const transformedData = await transformData(response.data)
      setTransformedData(transformedData)
      setTotalRecords(response.total_items)
      fetchImagesForRows(transformedData)
    } finally {
      setLoading(false)
    }
  }

  const handleProviderCountryChange = (value) => {
    setProviderCountry(value)
  }

  const handleAttachmentRemove = (attachment) => {
    setDeletedAttachments((prev) => [...prev, attachment.id])
    setEditRecord((prev) => ({
      ...prev,
      resourceContents: prev.resourceContents.filter(
        (item) => item.id !== attachment.id
      ),
    }))
  }

  const handleToggleComments = async (record, enabled) => {
    try {
      setLoading(true)
      const updatedData = {
        postId: record.id,
        text: record.formattedText,
        comments_enabled: enabled,
      }
      await enableComment(updatedData)
      message.success(
        `Comments ${enabled ? 'enabled' : 'disabled'} successfully`
      )
      await fetchBotPostData(currentPage, pageSize, userId, providerCountry)
    } catch (error) {
      message.error('Failed to update comment setting.')
    } finally {
      setLoading(false)
    }
  }

  const handleTogglePinned = async (record) => {
    try {
      setLoading(true)
      await updateBotPost({
        id: record.id,
        text: record.text,
        type: record.type,
        pinned: !record.pinned,
      })
      await fetchBotPostData(currentPage, pageSize, userId, providerCountry)
    } catch (err) {
      message.error('Failed to update pinned status.')
    } finally {
      setLoading(false)
    }
  }

  const handleEditSave = async () => {
    if (!editRecord) return
    let formattedContent = editText
    for (const username in mentionMap) {
      const regex = new RegExp(`@${username}\\b`, 'g')
      const tag = `<mention userId="${mentionMap[username]}">@${username}</mention>`
      formattedContent = formattedContent.replace(regex, tag)
    }
    formattedContent = formattedContent.replace(
      /#([\p{L}\p{N}_]+)/gu,
      '<hashtag>#$1</hashtag>'
    )
    setLoading(true)
    try {
      const updatedData = {
        id: editRecord.id,
        text: formattedContent,
        type: editRecord.type,
        newAttachments,
        attachmentsToDelete: deletedAttachments?.map(
          (attachment) => attachment
        ),
      }
      await updateBotPost(updatedData)
      message.success('Post updated successfully!')
      setEditModalVisible(false)
      await fetchBotPostData(currentPage, pageSize, userId, providerCountry)
    } catch (error) {
      message.error('Failed to update post.')
    } finally {
      setLoading(false)
    }
  }

  const showEditModal = (record) => {
    setEditRecord(record)
    const mentionRegex = /<mention userId="(.*?)">@(.*?)<\/mention>/g
    const extractedMap = {}
    let plainText = record.text || ''
    plainText = plainText.replace(mentionRegex, (_, userId, username) => {
      extractedMap[username] = userId
      return `@${username}`
    })
    setMentionMap((prevMap) => ({ ...extractedMap, ...prevMap }))
    setEditText(record.formattedText)
    setNewAttachments([])
    setDeletedAttachments([])
    setEditModalVisible(true)
  }

  const convertToCustomFormat = (timestamp) => {
    const date = new Date(timestamp)
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const seconds = String(date.getSeconds()).padStart(2, '0')
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`
  }

  const fetchCountry = async () => {
    try {
      const data = await fetchCountryProvider()
      const countryOptions = data.data.map((item) => ({
        label: item.countryName,
        value: item.countryCode,
      }))
      setCountries(countryOptions)
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  const showDeleteConfirm = (record) => {
    confirm({
      title: 'Are you sure you want to delete this content?',
      content: 'Once deleted, this action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'No, Cancel',
      onOk: () => handleDelete(record),
      onCancel: () => {
        message.info('Deletion cancelled.')
      },
    })
  }
  const handleFileChange = ({ fileList }) => {
    setNewAttachments(fileList)
  }
  const showPrivacyConfirm = (record) => {
    const nextType = record.type === 'PRIVATE' ? 'Public' : 'Private'
    Modal.confirm({
      title: `Are you sure you want to make this post ${nextType.toLowerCase()}?`,
      content:
        nextType === 'Private'
          ? 'Once private, only you and selected users can view this post.'
          : 'Making this public will allow everyone to see it.',
      okText: `Yes, Make ${nextType}`,
      cancelText: 'Cancel',
      onOk: () => handleTogglePrivacy(record),
    })
  }

  const fetchUnread = async () => {
    if (!userId) return
    try {
      const res = await unreadCountNotification(userId)
      const count = Number(res?.data) || 0
      setUnreadCount(count)
    } catch (e) {
      console.error('Error fetching unread notifications', e)
      setUnreadCount(0)
    }
  }

  useEffect(() => {
    const fetchInitialData = async () => {
      await fetchCountry()
      const users = await fetchBotUser()
      if (users.length > 0) {
        setUserId(users[0].value)
      } else {
        fetchBotPostData(currentPage, pageSize, '', providerCountry)
      }
    }
    fetchInitialData()
  }, [])

  useEffect(() => {
    if (!userId) return
    fetchBotPostData(currentPage, pageSize, userId, providerCountry)
    fetchUnread()
  }, [currentPage, pageSize, userId, providerCountry])

  useEffect(() => {
    if (!notificationVisible) {
      fetchUnread()
    }
  }, [notificationVisible])

  return (
    <Spin spinning={loading}>
      <div className="p-6 bg-gray-100 min-h-screen max-w-2xl mx-auto">
        <div className="header-container" role="banner">
          <div className="selects-group">
            <Select
              placeholder="Select Country"
              value={providerCountry}
              onChange={handleProviderCountryChange}
              options={countries}
            />
            <Select
              placeholder="Username"
              allowClear
              options={userData}
              onChange={handleInputChange}
              value={userId}
              showSearch
            />
          </div>
          <div className="bell-group">
            <Badge count={unreadCount} overflowCount={99} showZero={false}>
              <Button
                type="link"
                icon={<BellOutlined style={{ fontSize: 20 }} />}
                onClick={() => setNotificationVisible(true)}
                aria-label="View notifications"
              />
            </Badge>
          </div>
        </div>

        {transformedData.length === 0 && !loading ? (
          <Card className="border border-gray-200 rounded-xl shadow-lg bg-white">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Text className="text-gray-500">No posts available</Text>
              }
            />
          </Card>
        ) : (
          <div className="space-y-8">
            {transformedData.map((post) => (
              <Card
                key={post.id}
                className="border border-gray-200 rounded-xl shadow-lg bg-white overflow-hidden"
                hoverable
                style={{ marginTop: 10 }}
              >
                <Tag
                  color={post.type === 'PRIVATE' ? 'red' : 'green'}
                  style={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    zIndex: 1,
                  }}
                >
                  {post.type === 'PRIVATE' ? 'Private' : 'Public'}
                </Tag>

                <div className="p-5 pt-8" style={{ marginTop: 10 }}>
                  <Text className="block mb-4 text-gray-800">
                    {post.formattedText}
                  </Text>

                  {post.loadingAttachments ? (
                    <div style={{ marginTop: 20, textAlign: 'center' }}>
                      <Spin />
                    </div>
                  ) : (
                    post.resourceContents?.length > 0 && (
                      <Image.PreviewGroup>
                        <Row
                          gutter={[8, 8]}
                          className="mb-4"
                          style={{ marginTop: 20 }}
                        >
                          {post.resourceContents.map((content, index) => {
                            const imageFailed = imageLoadErrors[content.content]
                            return (
                              <Col key={index} span={4}>
                                {!imageFailed ? (
                                  <Image
                                    src={content.content}
                                    alt="Attachment"
                                    width={120}
                                    height={120}
                                    style={{
                                      objectFit: 'cover',
                                      borderRadius: 8,
                                    }}
                                    preview={{
                                      maskClassName: 'custom-image-mask',
                                    }}
                                    onError={() =>
                                      setImageLoadErrors((prev) => ({
                                        ...prev,
                                        [content.content]: true,
                                      }))
                                    }
                                  />
                                ) : (
                                  <a href={content.content} download>
                                    <Button
                                      icon={<DownloadOutlined />}
                                      size="small"
                                      className="w-32"
                                    >
                                      Download
                                    </Button>
                                  </a>
                                )}
                              </Col>
                            )
                          })}
                        </Row>
                      </Image.PreviewGroup>
                    )
                  )}

                  <div
                    className="flex justify-between text-gray-600 mb-3 font-medium"
                    style={{ marginTop: 15 }}
                  >
                    <Text style={{ color: '#1890ff' }}>
                      {post.likes == null ? 0 : post.likes}
                      <span className="ml-1">
                        {'  '}
                        <LikeOutlined style={{ color: '#1890ff' }} />
                      </span>
                    </Text>
                    <Text
                      style={{ marginLeft: 15, color: '#1890ff' }}
                      className="hover:underline cursor-pointer"
                      onClick={() => handleClickOpen(post)}
                    >
                      {post.comments == null ? 0 : post.comments}
                      <span className="ml-1">
                        {'  '}
                        <CommentOutlined style={{ color: '#1890ff' }} />
                      </span>
                    </Text>
                    <Text style={{ marginLeft: 15, color: '#1890ff' }}>
                      {post.shares == null ? 0 : post.shares}
                      <span className="ml-1">
                        {'  '}
                        <ShareAltOutlined style={{ color: '#1890ff' }} />
                      </span>
                    </Text>
                  </div>

                  <div
                    className="flex items-center justify-between pt-3 border-t border-gray-200"
                    style={{ marginTop: 10 }}
                  >
                    <Space>
                      <Switch
                        checked={post.comments_enabled}
                        onChange={(checked) =>
                          handleToggleComments(post, checked)
                        }
                        size="small"
                      />
                      <Text className="text-gray-600">Enable Comments</Text>
                    </Space>
                    <Space>
                      <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => showEditModal(post)}
                        className="text-gray-600"
                      >
                        Edit
                      </Button>
                      <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => showDeleteConfirm(post)}
                      >
                        Delete
                      </Button>
                      <div className="flex items-center gap-2 mb-2">
                        <Button
                          type="link"
                          icon={
                            post.type === 'PRIVATE' ? (
                              <UnlockOutlined style={{ color: '#389e0d' }} />
                            ) : (
                              <LockOutlined style={{ color: '#cf1322' }} />
                            )
                          }
                          onClick={() => showPrivacyConfirm(post)}
                          className="text-blue-600"
                        >
                          {post.type === 'PRIVATE'
                            ? 'Make Public'
                            : 'Make Private'}
                        </Button>

                        <Button
                          type="link"
                          icon={
                            post.pinned ? (
                              <PushpinFilled style={{ color: '#fa541c' }} />
                            ) : (
                              <PushpinOutlined />
                            )
                          }
                          onClick={() => handleTogglePinned(post)}
                        >
                          {post.pinned ? 'Unpin' : 'Pin'}
                        </Button>
                      </div>
                    </Space>
                  </div>
                  <div
                    className="flex items-center mb-3"
                    style={{ marginTop: 10 }}
                  >
                    <div>
                      <Text
                        style={{
                          fontStyle: 'italic',
                          marginTop: 8,
                          fontSize: '12px',
                          textAlign: 'right',
                        }}
                      >
                        {convertToCustomFormat(post.created_on)}
                      </Text>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
        <div
          className="mt-8 flex justify-between items-center"
          style={{ marginTop: 10 }}
        >
          {transformedData.length > 0 && (
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={totalRecords}
              onChange={(page, size) => {
                setCurrentPage(page)
                setPageSize(size)
              }}
              pageSizeOptions={['10', '25', '50', '100']}
              showSizeChanger
            />
          )}
        </div>
      </div>
      <Modal
        visible={previewVisible}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width="40%"
        className="rounded-lg"
        style={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}
        bodyStyle={{ padding: 0, textAlign: 'center' }}
      >
        <img
          alt="Preview"
          className="w-full h-auto max-h-[70vh] object-contain rounded-md"
          src={previewImage}
        />
      </Modal>
      <Modal
        title="Edit Post"
        visible={editModalVisible}
        onOk={handleEditSave}
        onCancel={() => setEditModalVisible(false)}
        okText="Save"
      >
        <div className="relative mt-2" style={{ marginTop: 10 }}>
          <MentionEmojiInput
            value={editText}
            onChange={setEditText}
            fetchMentions={async (query) => {
              const res = await getUsers(0, 10, query, providerCountry)
              return res.data.map((user) => ({
                label: user.userName,
                value: user.userName,
                rawId: user.userId,
              }))
            }}
            placeholder="Edit your post here..."
            onMentionMapChange={setMentionMap}
          />
        </div>
        <h4 className="mt-4">Current Attachments:</h4>
        <Row gutter={[16, 16]} style={{ marginTop: 8, marginBottom: 12 }}>
          {editRecord?.resourceContents?.map((attachment, index) => {
            const imageFailed = imageLoadErrors[attachment.content]
            return (
              <Col key={index} span={6}>
                <div
                  className="flex flex-col items-center justify-center"
                  style={{ gap: 6 }}
                >
                  {!imageFailed ? (
                    <Image
                      src={attachment.content}
                      alt="Attachment"
                      className="w-12 h-12 object-cover rounded-md border border-gray-200"
                      onError={() =>
                        setImageLoadErrors((prev) => ({
                          ...prev,
                          [attachment.content]: true,
                        }))
                      }
                      width={80}
                      height={80}
                    />
                  ) : (
                    <a href={attachment.content} download>
                      <Button icon={<DownloadOutlined />} size="small">
                        Download
                      </Button>
                    </a>
                  )}
                  <Button
                    type="link"
                    danger
                    onClick={() => handleAttachmentRemove(attachment)}
                  >
                    Remove
                  </Button>
                </div>
              </Col>
            )
          })}
        </Row>
        <h4 className="mt-4">Add New Attachments:</h4>
        <Upload
          beforeUpload={() => false}
          className="custom-upload"
          showUploadList={{ showRemoveIcon: true, showPreviewIcon: false }}
          fileList={newAttachments}
          onChange={handleFileChange}
          listType="picture-card"
          accept={allowedFileTypes.join(',')}
          multiple
        >
          Attach File
        </Upload>
      </Modal>
      <Modal
        title="Notifications"
        visible={notificationVisible}
        onCancel={() => setNotificationVisible(false)}
        footer={null}
        width={600}
      >
        <Notification botUserId={userId} />
      </Modal>
    </Spin>
  )
}

export default BotPost
